/** @jsxImportSource @emotion/react */
'use memo'

import css from '@emotion/css/macro'
import React from 'react'
import colors from '@sportninja/common/constants/appColors'
import { PRIMARY_BUTTON_CONTENT } from '@sportninja/common/constants/buttonColors'
import { TIMES_OF_DAY } from '@sportninja/common/constants/timesOfDay'
import type { TimeOfDay } from '@sportninja/common/features/profile/finderSettings/types'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import {
  font,
  weightMedium,
  weightSemiBold,
  paragraphSmall,
} from 'src/components/css'

interface Props {
  value: TimeOfDay[]
  onToggleTime: (time: TimeOfDay) => void
}

/** Mirrors DayChips — the time-of-day counterpart of Available Days. */
const TimeChips: React.FC<Props> = ({ value, onToggleTime }) => (
  <div>
    <div css={labelStyle}>{capitalizeWords('Available Times')}</div>
    <div css={rowStyle}>
      {TIMES_OF_DAY.map((time) => {
        const active = value.includes(time.value)
        return (
          <button
            key={time.value}
            type='button'
            aria-pressed={active}
            css={[chipStyle, active && chipActiveStyle]}
            onClick={() => onToggleTime(time.value)}
          >
            {time.name}
          </button>
        )
      })}
    </div>
  </div>
)

export default TimeChips

// ─── Styles ────────────────────────────────────────────────────────────────

const labelStyle = css`
  ${paragraphSmall}
  ${weightSemiBold}
  ${font.body}
  color: ${colors.WHITE};
  margin-bottom: 8px;
`

const rowStyle = css`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`

// Unselected: outlined (transparent fill + border + light text). Active:
// PRIMARY fill, with the label color from the shared tenant-aware config.
const chipStyle = css`
  ${paragraphSmall}
  ${weightMedium}
  ${font.body}
  padding: 8px 12px;
  border-radius: 8px;
  border: 1px solid ${colors.SOFT_STEEL};
  background-color: transparent;
  color: ${colors.WHITE};
  cursor: pointer;
`

const chipActiveStyle = css`
  ${weightSemiBold}
  background-color: ${colors.PRIMARY};
  border-color: ${colors.PRIMARY};
  color: ${PRIMARY_BUTTON_CONTENT.text};
`
